<template>
  <div class="resend-tickets-root">
    <GlobalNavigation />

    <main class="py-8 mb-16">

      <BaseContainer>
        <div class="text-center">
          <BaseButton color="muted" to="/">
            <DropdownIcon class="rotate-90 h-4 w-4 mr-2 fill-text" />
            {{ $t('common.back_to_overview') }}
          </BaseButton>
    
          <h1 class="mb-6 mt-6 h1">{{ $t('resend_ticket.title') }}</h1>
        </div>
      </BaseContainer>

      <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
        <BaseContainer>
          <div class="max-w-[450px] mx-auto">
  
            <p>{{ $t('resend_ticket.description') }}</p>

            <div class="mb-4 mt-4">
              <InputText
                id="email"
                name="email"
                type="email"
                :rules="rules.email"
                v-model="form.email"
                :placeholder="$t('resend_ticket.email_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <BaseButton
              class="w-full relative group"
              color="primary"
              size="lg"
              type="submit"
              :disabled="isSubmitting"
            >
              <span class="w-full flex items-center justify-center">
                {{ $t('resend_ticket.submit') }}
                <ArrowRightIcon
                  class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
                />
              </span>
            </BaseButton>

            <div v-if="errorMessage" class="text-sm block pt-2 text-error">
              {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="text-sm block pt-2 text-success">
              {{ successMessage }}
            </div>
          </div>
        </BaseContainer>
      </Form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { resendTickets } from '@/api/resendTickets';
import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
import DropdownIcon from '@/components/icons/DropdownIcon.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseContainer from '@/components/ui/BaseContainer.vue';
import InputText from '@/components/ui/inputs/InputText.vue';

const { t } = useI18n();

useHead({
  title: t('resend_ticket.head_title'),
  meta: [
    { name: 'description', content: t('resend_ticket.description') },
    { property: 'og:title', content: t('resend_ticket.head_title') },
    { property: 'og:description', content: t('resend_ticket.description') },
  ],
});

const form = reactive({ email: '' });

const rules = {
  email: yup
    .string()
    .email(t('validation.email'))
    .required(t('validation.required')),
};

const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const isSubmitting = ref(false);

const handleSubmit: SubmissionHandler = async (values) => {
  const { email } = values as { email: string };

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const result = await resendTickets(email);

    if (result.status === 'ok') {
      successMessage.value = t('resend_ticket.success_message');
    } else if (result.error === 'resend_tickets_repeat_too_soon') {
      errorMessage.value = t('resend_ticket.error_too_soon');
    } else if (result.error === 'resend_tickets_repeat_invalid_email') {
      errorMessage.value = t('resend_ticket.error_invalid_email');
    } else {
      errorMessage.value = t('resend_ticket.error_generic');
    }
  } catch {
    errorMessage.value = t('resend_ticket.error_generic');
  } finally {
    isSubmitting.value = false;
  }
};

const onInvalidSubmit: InvalidSubmissionHandler = () => {
  // vee-validate handles field-level errors; nothing extra needed here
};
</script>

<style>
.min-h-screen:has( .resend-tickets-root) {
  min-height: calc(100vh - 538px) !important;
}
</style>